import { Left, Right } from 'src/assets'
import React, {
  useCallback,
  ReactNode,
  useEffect,
  useState,
  useRef,
} from 'react'

interface CarouselProps {
  interval?: number
  hideDots?: boolean
  delay?: number
  children?: ReactNode[]
  images?: string[]
  hideActionButtons?: boolean
  className?: string
}

const Carousel: React.FC<CarouselProps> = ({
  hideActionButtons,
  className,
  children = [],
  interval = 5000,
  hideDots,
  delay = 0,
  images,
}) => {
  const [currentChildIndex, setCurrentChildIndex] = useState(0)
  const childrenCount = children ? React.Children.count(children) : 0
  const imagesCount = images ? images.length : 0
  const totalSlides = childrenCount || imagesCount
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const carouselClasses = [
    'relative',
    'w-full',
    'h-full',
    'overflow-hidden',
    'transition-all',
    'duration-500',
    'rounded-md',
  ]
  if (className) carouselClasses.push(className)
  if (totalSlides < 2) {
    hideActionButtons = true
    hideDots = true
  }

  const nextSlide = useCallback(() => {
    setCurrentChildIndex((prevIndex) =>
      prevIndex === totalSlides - 1 ? 0 : prevIndex + 1
    )
  }, [totalSlides])

  const prevSlide = () =>
    setCurrentChildIndex((prevIndex) =>
      prevIndex === 0 ? totalSlides - 1 : prevIndex - 1
    )

  const goToSlide = (index: number) => setCurrentChildIndex(index)

  useEffect(() => {
    if (totalSlides === 2 || interval === 0) return
    const timeoutId = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        nextSlide()
      }, interval)
    }, delay)

    return () => {
      if (totalSlides < 2 || interval === 0) return
      clearTimeout(timeoutId)
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [delay, interval, totalSlides, nextSlide])

  return (
    <div className={carouselClasses.join(' ')}>
      {!hideActionButtons && (
        <button
          onClick={prevSlide}
          className={
            'absolute z-5 top-0 left-0 h-full z-10 w-[50px] focus:outline-none flex-center transition-opacity duration-300 hover:opacity-100 opacity-0 bg-gradient-to-l from-transparent to-[rgba(0,0,0,0.3)] text-gray-500'
          }
        >
          <Left className='fill-white h-[25px] w-[25px]' />
        </button>
      )}
      {!hideActionButtons && (
        <button
          onClick={nextSlide}
          className={
            'absolute z-5 top-0 right-0 h-full z-10 w-[50px] focus:outline-none flex-center transition-opacity duration-300 hover:opacity-100 opacity-0 bg-gradient-to-r from-transparent to-[rgba(0,0,0,0.3)] text-gray-500'
          }
        >
          <Right className='fill-white h-[25px] w-[25px]' />
        </button>
      )}
      <div
        className='relative w-full h-full max-h-full flex items-center duration-1000 group-hover:scale-110 transition'
        style={{ transform: `translateX(-${currentChildIndex * 100}%)` }}
      >
        {images
          ? images.map((image, index) => (
              <img
                key={index}
                src={image}
                width={0}
                height={0}
                sizes='100vw'
                style={{ width: '100%', height: '100%' }}
                alt={`carousel-${index}`}
                className='object-cover'
              />
            ))
          : children.map((child, index) => (
              <div
                className='
                  min-w-full w-full min-h-full h-full
                  object-cover flex justify-center items-center'
                key={index}
              >
                {child}
              </div>
            ))}
      </div>
      {!hideDots && (
        <div className='absolute z-5 bottom-0 left-0 right-0 flex justify-center mb-4'>
          {Array.from({ length: totalSlides }, (_, index) => (
            <div
              key={index}
              className={`w-2 h-2 mx-1 rounded-full cursor-pointer transition-colors duration-1000 ${
                index === currentChildIndex ? 'bg-dark-gray' : 'bg-white'
              }`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Carousel
